<script setup>
import {computed, watch, onBeforeUnmount, ref} from 'vue'
import {useComponentsStore} from '@/stores/components'
import {useFieldsetsStore} from '@/stores/fieldsets'
import {useComponentStructureStore} from '@/stores/componentStructure'
import {useItemDataStore} from '@/stores/itemData'
import {useViewStore} from '@/stores/view'
import moment from "moment"
import {Offcanvas} from "bootstrap"

const props = defineProps({
    viewId: String,
    filterKey: Number,
    offCanvasId: String
})

// Dynamic View Store
const viewStore = useViewStore(props.viewId)

// Pinia Stores
const componentsStore = useComponentsStore()
const fieldsetsStore = useFieldsetsStore()
const componentStructureStore = useComponentStructureStore()
const itemDataStore = useItemDataStore()

// Keep track of unmount state for extra async sanity checks
let isDestroyed = false
onBeforeUnmount(() => {
    isDestroyed = true
})

// Computed properties
const filter = computed(() => {
    return viewStore.filters?.[props.filterKey] || null
})

const show = computed(() => {
    if (!filter.value) return false
    return filter.value.value !== undefined && filter.value.value !== ''
})

const tableName = computed(() => viewStore.tableName || null)

const componentId = computed(() => viewStore.componentId || null)

const fields = computed(() => {
    if (!componentId.value) return []
    return componentStructureStore.get(componentId.value) || []
})

// Computed property using Pinia fieldsets store
const firstFieldsetId = computed(() => {
    if (!componentId.value || !fieldsetsStore.items) return null
    const fieldset = fieldsetsStore.items.find(o => o.componentId === componentId.value)
    return fieldset ? fieldset.id : null
})

const columns = computed(() => {
    const idColumn = {
        fieldsetId: firstFieldsetId.value,
        columnName: 'id',
        title: 'ID',
        type: 'number'
    }

    return [idColumn].concat(fields.value)
})

const columnTitle = computed(() => {
    if (!filter.value?.columnName) return ''
    return columns.value.find(o => o.columnName === filter.value.columnName)?.title || ''
})

// Pure Vue replacement for useAsyncState
const value = ref('')

watch(
    [filter.value, columns.value],
    async () => {
        if (!filter.value) {
            value.value = ''
            return
        }
        const val = filter.value.value

        let result = val

        switch (filter.value.type) {
            case 'checkbox':
                result = val ? 'checked' : 'unchecked'
                break

            case 'date':
                result = val ? moment(val).format('DD.MM.YY') : ''
                break

            case 'datetime-local':
                result = val ? moment(val).format('DD.MM.YY HH:mm') : ''
                break

            case 'internalLink':
                if (!val) {
                    result = ''
                    break
                }
                try {
                    const page = await itemDataStore.get({tableName: 'pages', id: val})
                    result = page ? page.title : ''
                } catch {
                    result = ''
                }
                break

            case 'relationshipOneToMany':
            case 'relationshipManyToMany':
                if (!val || !columns.value.length) {
                    result = ''
                    break
                }
                const column = columns.value.find(o => o.columnName === filter.value.columnName)
                if (!column || !column.categoryComponentId) {
                    result = val
                    break
                }

                const items = componentsStore.items || []
                const component = items.find(o => o.id === column.categoryComponentId)
                if (!component || !component.tableName) {
                    result = val
                    break
                }

                try {
                    const item = await itemDataStore.get({tableName: component.tableName, id: val})
                    result = item ? (item.name || item.title || val) : val
                } catch {
                    result = val
                }
                break

            default:
                result = val
        }

        if (!isDestroyed) {
            value.value = result
        }
    },
    {immediate: true}
)

// Methods
const openOffcanvas = () => {
    const offcanvas = document.getElementById(props.offCanvasId)
    if (!offcanvas) return

    new Offcanvas(offcanvas).show()
    offcanvas.addEventListener('shown.bs.offcanvas', e => {
        const filterNode = offcanvas.querySelectorAll('form > div')[props.filterKey]
        if (!filterNode) return

        const filterField = filterNode.querySelector('[data-search-filter-field]')
        if (!filterField) return

        if (filterField.tagName === 'BUTTON') {
            filterField.focus()
        } else if (filterField?.select) {
            filterField?.select()
        }
    }, {once: true})
}

const resetFilter = () => {
    if (filter.value) {
        viewStore.resetFilter(props.filterKey)
    }
}
</script>

<template>
    <!-- Safeguarded template paths from reading properties of null values -->
    <a href @click.prevent v-if="show && filter" role="button" @click="openOffcanvas" :aria-controls="offCanvasId"
       class="filter d-inline-flex align-items-center px-3 py-1 rounded-5 border text-bg-light position-relative small text-nowrap text-decoration-none focus-ring">
        <small>{{ columnTitle }}</small>

        <small class="mx-1 text-secondary">{{ filter.conditionText }}</small>

        <small v-if="filter.conditionText !== value">{{ value }}</small>

        <small @click.stop.prevent="resetFilter"
               class="filter-delete-btn btn btn-sm btn-light rounded-5 p-0 lh-1 position-absolute end-0">
            <i class="bi bi-x"/>
        </small>
    </a>
</template>

<style scoped>
span {
    font-size: .75rem;
}

.filter-delete-btn {
    visibility: hidden;
}

.filter:hover .filter-delete-btn {
    visibility: visible;
}
</style>